(function () {
	if (window.PaginationController) return;

	class PaginationController {
	constructor(options = {}) {
		this.paginationSelector = options.paginationSelector || '[data-pagination]';
		this.paginationSectionId = options.paginationSectionId;
		this.contentSectionId = options.contentSectionId;
		this.containerSelector = options.containerSelector;
		this.sourceSelector = options.sourceSelector || this.containerSelector;
		this.append = options.append ?? true;
		this.freezeOnReplace = options.freezeOnReplace ?? true;
		this.getCurrentPage = options.getCurrentPage;
		this.getTotalPages = options.getTotalPages;
		this.onPageChange = options.onPageChange;

		this._onClick = this.handleClick.bind(this);
		this._onInfiniteNext = this.handleInfiniteNext.bind(this);
		this._onPageShow = this.handlePageShow.bind(this);
		this._clickBound = false;
		this._infiniteBound = false;
		this._pageShowBound = false;
		this._restoring = false;

		if (!window.__paginationControllers) {
			window.__paginationControllers = new Map();
		}
		const existing = window.__paginationControllers.get(this.paginationSectionId);
		if (existing) {
			existing.destroy();
		}
		window.__paginationControllers.set(this.paginationSectionId, this);

		this.init();
	}

	init() {
		this.bindClicks();
		this.bindInfinite();
		this.bindPageShow();
		this.updateInfiniteState();
	}

	bindClicks() {
		if (this._clickBound) return;
		document.body.addEventListener('click', this._onClick);
		this._clickBound = true;
	}

	handleClick = async (e) => {
		const link = e.target.closest('.pagination a[href]');
		if (!link || link.classList.contains('disabled')) return;

		e.preventDefault();

		const url = new URL(link.href);
		const page = parseInt(url.searchParams.get('page'), 10);
		if (isNaN(page)) return;

		this.freezeLayout();

		await this.onPageChange?.(page, { append: false });
		await this.updatePagination(page);

		this.unfreezeLayout();
		//this.scrollToPagination();
	}

	bindInfinite() {
		if (this._infiniteBound) return;
		document.addEventListener('infinite:next', this._onInfiniteNext);
		this._infiniteBound = true;
	}

	bindPageShow() {
		if (this._pageShowBound) return;
		window.addEventListener('pageshow', this._onPageShow);
		this._pageShowBound = true;
	}

	handleInfiniteNext = async () => {
		const current = this.getInfiniteCurrentPage();
		const nextPage = current + 1;

		await this.onPageChange?.(nextPage, { append: true });
		await this.updatePagination(nextPage);
		//this.scrollToPagination();
	}

	handlePageShow(event) {
		try {
			this.restoreFromStorage();
		} catch (error) {
			// ignore
		}
	}

	async restoreFromStorage() {
		if (this._restoring) return;
		const targetPage = this.getStoredPage();
		if (!targetPage || targetPage <= 1) return;
		this._restoring = true;
		this.freezeLayout();
		try {
			await this.onPageChange?.(targetPage, { append: false });
			await this.updatePagination(targetPage);
		} finally {
			this.unfreezeLayout();
			this.updateInfiniteState();
			this.clearStoredPage();
			this._restoring = false;
		}
	}

	destroy() {
		if (this._clickBound) {
			document.body.removeEventListener('click', this._onClick);
			this._clickBound = false;
		}

		if (this._infiniteBound) {
			document.removeEventListener('infinite:next', this._onInfiniteNext);
			this._infiniteBound = false;
		}
		if (this._pageShowBound) {
			window.removeEventListener('pageshow', this._onPageShow);
			this._pageShowBound = false;
		}
		if (window.__paginationControllers?.get(this.paginationSectionId) === this) {
			window.__paginationControllers.delete(this.paginationSectionId);
		}
	}

	freezeLayout() {
		if (!this.freezeOnReplace) return;

		this.container = document.querySelector(this.containerSelector);
		if (!this.container) return;

		this.frozenHeight = this.container.offsetHeight;
		this.container.style.minHeight = this.frozenHeight + 'px';

		this.container.style.opacity = '0.4';
		this.container.style.pointerEvents = 'none';
	}

	unfreezeLayout() {
		if (!this.container) return;

		this.container.style.minHeight = '';
		this.container.style.opacity = '';
		this.container.style.pointerEvents = '';

		this.container = null;
		this.frozenHeight = null;
	}

	scrollToPagination(offset = 150) {
		const paginationBlock = document.querySelector('[data-pagination]');
		if (!paginationBlock) return;

		const rect = paginationBlock.getBoundingClientRect();
		const scrollTarget = window.scrollY + rect.bottom - window.innerHeight + offset;
		window.scrollTo({
			top: scrollTarget,
			behavior: "smooth"
		});
	}

	async loadPage(page, { append = false } = {}) {
		const url = new URL(window.location.href);
		url.searchParams.set('page', page);

		const res = await fetch(
			`${url}&section_id=${this.contentSectionId}`
		);
		const html = await res.text();

		const doc = new DOMParser().parseFromString(html, 'text/html');
		const newContent = doc.querySelector(this.sourceSelector);
		const currentContent = document.querySelector(this.containerSelector);

		if (!newContent || !currentContent) return;

		if (append) {
			currentContent.insertAdjacentHTML(
				'beforeend',
				newContent.innerHTML
			);
		} else {
			currentContent.innerHTML = newContent.innerHTML;
		}
	}


	async updatePagination(page) {
		const pagination = document.querySelector(this.paginationSelector);
		if (!pagination) return;

		const url = new URL(window.location.href);
		url.searchParams.set('page', page);

		const res = await fetch(`${url}&section_id=${this.paginationSectionId}`);
		const html = await res.text();

		const doc = new DOMParser().parseFromString(html, 'text/html');
		const newPagination = doc.querySelector(this.paginationSelector);

		if (newPagination) {
			pagination.replaceWith(newPagination);
		}

		this.syncPrevNext();
		this.syncActive();
		const totalFromDom = Number(document.querySelector(this.paginationSelector)?.dataset?.totalPages);
		const totalPages = Number.isFinite(totalFromDom) && totalFromDom > 0 ? totalFromDom : (this.getTotalPages?.() ?? 1);
		const infinite = document.querySelector('infinite-pagination');
		if (infinite) {
			infinite.dataset.currentPage = String(page);
			infinite.dataset.totalPages = String(totalPages);
		}
		this.updateInfiniteState();
		this.setStoredPage(page);

		const total = this.getTotalPages?.() ?? 1;

		const paginationRoot = document.querySelector(this.paginationSelector);
		if (paginationRoot) {
			paginationRoot.style.display = total > 1 ? '' : 'none';
		}
	}

	syncActive() {
		const currentPage = this.getCurrentPage();

		document.querySelectorAll('.pagination-list a').forEach(a => {
			const page = parseInt(a.textContent.trim());
			if (isNaN(page)) return;

			a.classList.toggle('active', page === currentPage);
			a.toggleAttribute('aria-current', page === currentPage);
		});
	}

	syncPrevNext() {
		const current = this.getCurrentPage();
		const total = this.getTotalPages();

		const prev = document.querySelector('.pagination-prev a');
		const next = document.querySelector('.pagination-next a');

		if (prev) {
			prev.classList.toggle('disabled', current <= 1);
		}

		if (next) {
			next.classList.toggle('disabled', current >= total);
		}
	}

	updateInfiniteState() {
		const infinite = document.querySelector('infinite-pagination');
		if (!infinite) return;

		infinite.dataset.currentPage = String(this.getInfiniteCurrentPage());
		infinite.dataset.totalPages = String(this.getInfiniteTotalPages());

		infinite.updateState();
	}

	getInfiniteCurrentPage() {
		const infinite = document.querySelector('infinite-pagination');
		const value = Number(infinite?.dataset?.currentPage);
		return Number.isFinite(value) && value > 0 ? value : 1;
	}

	getInfiniteTotalPages() {
		const infinite = document.querySelector('infinite-pagination');
		const value = Number(infinite?.dataset?.totalPages);
		return Number.isFinite(value) && value > 0 ? value : 1;
	}

	getStorageKey() {
		const url = new URL(window.location.href);
		const path = url.pathname.endsWith('/') && url.pathname.length > 1 ? url.pathname.slice(0, -1) : url.pathname;
		return `pagination:${this.paginationSectionId}:${path}`;
	}


	getStoredPage() {
		try {
			const raw = window.sessionStorage.getItem(this.getStorageKey());
			const value = Number(raw);
			return Number.isFinite(value) ? value : NaN;
		} catch (error) {
			return NaN;
		}
	}

	clearStoredPage() {
		try {
			window.sessionStorage.removeItem(this.getStorageKey());
		} catch (error) {
			// ignore
		}
	}

	setStoredPage(page) {
		try {
			if (!page || page <= 1) {
				window.sessionStorage.removeItem(this.getStorageKey());
				return;
			}
			window.sessionStorage.setItem(this.getStorageKey(), String(page));
		} catch (error) {
			// ignore storage errors
		}
	}
	}

	window.PaginationController = PaginationController;

// Infinite pagination

	if (!customElements.get('infinite-pagination')) {
		customElements.define('infinite-pagination', class InfinitePagination extends HTMLElement {
			connectedCallback() {
				this.button = this.querySelector('a');
				if (!this.button) return;

				if (!this._onClick) {
					this._onClick = this.onClick.bind(this);
				}
				this.button.addEventListener('click', this._onClick);
				this.updateState();
			}

			disconnectedCallback() {
				if (this.button && this._onClick) {
					this.button.removeEventListener('click', this._onClick);
				}
			}

			updateState() {
			let current = Number(this.dataset.currentPage || 0);
			let total = Number(this.dataset.totalPages || 0);
			if (!current || !total) {
				const pagination = document.querySelector('.pagination');
				const domCurrent = Number(pagination?.dataset?.currentPage || 0);
				const domTotal = Number(pagination?.dataset?.totalPages || 0);
				current = current || domCurrent || 1;
				total = total || domTotal || 1;
				this.dataset.currentPage = String(current);
				this.dataset.totalPages = String(total);
			}

				if (total <= 1 || current >= total) {
					this.classList.add('hidden');
				} else {
					this.classList.remove('hidden');
				}
			}

			onClick(e) {
				e.preventDefault();
				this.dispatchEvent(
					new CustomEvent('infinite:next', { bubbles: true })
				);
			}
		});
	}
})();
